// A casca que as DUAS telas vestem: o painel do cliente (`src/cliente`) e o console do dono
// (`src/client`). Cabecalho com a marca, menu que vira fileira no celular, conteudo e rodape com a
// razao social.
//
// 🔴 ESTA CASCA NAO TRADUZ NADA. Todo texto chega pronto de quem chama. O painel do cliente fala
// tres idiomas e o console fala um so; se a casca chamasse `t()`, ou o console carregaria um
// dicionario que nao usa, ou o painel perderia os idiomas no caminho. Alem disso o teste de
// varredura dos idiomas le `src/cliente/App.tsx` para cobrar tradutor e chave orfa — um `t()` que
// se mudasse para ca sairia do alcance dele e passaria a ser cobrado como chave sem uso.
import type { LucideIcon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
  aplicar,
  guardarPreferencia,
  lerPreferencia,
  observarSistema,
  type Preferencia,
  resolver,
  sistemaEscuro,
  type Tema,
} from "./tema";
import { cn } from "./ui";

// —— Tema —————————————————————————————————————————————————————————————————————

// A preferência de tema, viva. Devolve também qual tema está valendo AGORA, que é o que decide qual
// arquivo de logo usar — a logo tem tinta clara e tinta escura, e a errada some no fundo.
export function useTema(): [Preferencia, (p: Preferencia) => void, Tema] {
  const [preferencia, setPreferencia] = useState<Preferencia>(lerPreferencia);
  const [tema, setTema] = useState<Tema>(() =>
    resolver(lerPreferencia(), sistemaEscuro()),
  );

  useEffect(() => {
    const atual = resolver(preferencia, sistemaEscuro());
    aplicar(atual);
    setTema(atual);
  }, [preferencia]);

  // Acompanha o sistema enquanto a escolha for `auto`. Sem isto, quem está em automático só veria o
  // sistema ter mudado depois de recarregar a página.
  useEffect(() => {
    if (preferencia !== "auto") return;
    return observarSistema((escuro) => {
      aplicar(escuro ? "dark" : "light");
      setTema(escuro ? "dark" : "light");
    });
  }, [preferencia]);

  const escolher = useCallback((p: Preferencia) => {
    setPreferencia(p);
    guardarPreferencia(p);
  }, []);

  return [preferencia, escolher, tema];
}

// —— Marca e rodapé ———————————————————————————————————————————————————————————

// 🔴 "light" e "dark" aqui são a TINTA, não o fundo: `logo-light.png` é a logo preta, e é ela que vai
// no tema claro. Trocar os dois faz a logo sumir no fundo — e o erro só aparece em um dos temas.
export function Marca({
  tema,
  className,
}: {
  tema: Tema;
  className?: string;
}) {
  return (
    <img
      src={tema === "dark" ? "/assets/logo.png" : "/assets/logo-light.png"}
      alt="IndicaFácil.AI"
      className={cn("h-7 w-auto", className)}
    />
  );
}

// A RAZAO SOCIAL, nao a marca. O nome fantasia a pessoa ja le no topo; o rodape e onde ela vê com
// QUEM contratou — o mesmo nome que vai aparecer na nota, no contrato e na fatura do cartao. Sem
// isto, uma cobranca de "IndicaFacil.AI Tecnologia e Servicos Digitais" chega como nome estranho e
// vira contestacao.
export const RAZAO_SOCIAL = "IndicaFácil.AI Tecnologia e Serviços Digitais";

export function Rodape({ className }: { className?: string }) {
  return (
    <footer
      className={cn(
        "shrink-0 px-4 py-5 text-center text-text-muted text-xs",
        className,
      )}
    >
      © {new Date().getFullYear()} {RAZAO_SOCIAL}
    </footer>
  );
}

// —— A casca ——————————————————————————————————————————————————————————————————

// `rotulo` chega TRADUZIDO. Ver o aviso no topo do arquivo.
export interface ItemDeMenu<P extends string> {
  id: P;
  rotulo: string;
  Icone: LucideIcon;
}

export function Casca<P extends string>({
  tema,
  acoes,
  menu,
  pagina,
  irPara,
  rodapeDoMenu,
  larguraDoConteudo = "max-w-3xl",
  children,
}: {
  tema: Tema;
  // O canto direito do cabeçalho. É o que mais difere entre as duas telas — o painel põe o e-mail de
  // quem entrou e o botão de sair; o console não tem identidade para mostrar (a sessão dele é um
  // token, não uma pessoa) e põe a versão do produto.
  acoes?: ReactNode;
  menu: ReadonlyArray<ItemDeMenu<P>>;
  pagina: P;
  irPara: (p: P) => void;
  // Fica ancorado no RODAPÉ do menu em tela larga, e some no celular. É onde o painel põe a ajuda.
  rodapeDoMenu?: ReactNode;
  larguraDoConteudo?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex shrink-0 items-center justify-between gap-3 border-border border-b px-4 py-3">
        <Marca tema={tema} />
        <div className="flex items-center gap-2">{acoes}</div>
      </header>

      <div className="flex flex-1 flex-col md:flex-row">
        {/* 🔴 No celular o menu vira uma fileira rolável acima do conteúdo, e não uma barra lateral
            espremida: barra lateral em tela estreita come metade da largura útil. */}
        <nav className="shrink-0 border-border border-b p-2 md:flex md:w-56 md:flex-col md:border-r md:border-b-0">
          <div className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
            {menu.map(({ id, rotulo, Icone }) => (
              <button
                key={id}
                type="button"
                onClick={() => irPara(id)}
                aria-current={pagina === id ? "page" : undefined}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                  pagina === id
                    ? "bg-bg-tertiary font-medium text-text-primary"
                    : "text-text-secondary hover:bg-bg-tertiary hover:text-text-primary",
                )}
              >
                <Icone className="h-4 w-4 shrink-0" aria-hidden="true" />
                {rotulo}
              </button>
            ))}
          </div>

          {/* 🔴 `mt-auto` ancora o bloco no RODAPE do menu. Colado nos itens ele vira mais um item de
              navegacao e compete com eles; no rodape, fica onde quem procura ajuda olha. */}
          {rodapeDoMenu ? (
            <div className="mt-4 hidden border-border border-t pt-3 md:mt-auto md:block">
              {rodapeDoMenu}
            </div>
          ) : null}
        </nav>

        <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
          <div
            className={cn(
              "mx-auto flex w-full flex-col gap-5",
              larguraDoConteudo,
            )}
          >
            {children}
          </div>
        </main>
      </div>

      <Rodape className="border-border border-t" />
    </div>
  );
}
